/**
 * Almikia Epic E5 (TASK-034): constants and path rule shared by the Supabase
 * and FakeRest operation-documents providers.
 */

/** Private bucket behind `document_versions` (never a public link, always a signed URL). */
export const OPERATION_DOCUMENTS_BUCKET = "operation-documents";

/** A download link lives one minute: long enough to open it, short enough to not be shared. */
export const OPERATION_DOCUMENT_SIGNED_URL_EXPIRY_SECONDS = 60;

/**
 * `<operation_id>/<uuid>-<file_name>`: `register_document_version` requires the
 * path to start with the operation id. Only the key segment is sanitized
 * (diacritics stripped, anything outside `[A-Za-z0-9._-]` becomes `_`) because
 * Storage rejects keys with accents, `#`, `%`, etc. (InvalidKey); the original
 * name is kept separately as `p_file_name`.
 */
export function buildOperationDocumentStoragePath(
  operationId: string,
  fileName: string,
): string {
  const safeName = fileName
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]/g, "_");
  return `${operationId}/${crypto.randomUUID()}-${safeName}`;
}

/**
 * QE5-5: the moment a document may be soft-deleted, i.e. the latest
 * `retention_until` among the document and its live versions.
 */
export function getRetentionEnd(doc: {
  retention_until: string;
  versions?: ReadonlyArray<{
    retention_until: string;
    deleted_at?: string | null;
  }>;
}): Date {
  const times = [
    doc.retention_until,
    ...(doc.versions ?? [])
      .filter((version) => !version.deleted_at)
      .map((version) => version.retention_until),
  ].map((iso) => new Date(iso).getTime());
  return new Date(Math.max(...times));
}

/** dd/mm/aaaa in Mexico City time, as printed by the server's retention error. */
export function formatRetentionDate(date: Date): string {
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "America/Mexico_City",
  });
}

/** Spanish error of a soft delete attempted before the retention end (same text as the SQL). */
export const retentionDeleteMessage = (end: Date): string =>
  `No puedes eliminar este documento antes del fin de su retención (${formatRetentionDate(end)})`;
